// src/components/GeminiTldrCard.tsx
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface GeminiTldrCardProps {
    // Props controlling the card's state, passed from PostPage
    isVisible: boolean;
    isLoading: boolean;
    error: string | null;
    tldrContent: string | null;
    onDismiss: () => void; // Callback to notify parent when dismissed
}

const GeminiTldrCard: React.FC<GeminiTldrCardProps> = ({
    isVisible,
    isLoading,
    error,
    tldrContent,
    onDismiss
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string | number>('0px');

    // Calculate dynamic max-height for smooth animation
    useEffect(() => {
        if (isVisible && cardRef.current) {
            // Set max-height slightly larger than scrollHeight for smooth transition
            setMaxHeight(cardRef.current.scrollHeight + 20); // Add some buffer
        } else {
            setMaxHeight('0px');
        }
        // Re-calculate when visibility or content changes that affects height
    }, [isVisible, tldrContent, error, isLoading]);

    return isVisible ? (
        <div className="mb-4"> {/* Keep outer margin */}
            {/* TL;DR Card Content Area */}
            <div
                ref={cardRef}
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ maxHeight: maxHeight }}
                aria-live="polite"
                aria-hidden={!isVisible}
            >
                {/* Render content only if the card should be visible */}
                {isVisible && (
                    <div className="mt-2 p-4 rounded-lg border bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700/70 relative shadow-sm">
                        {/* Dismiss Button */}
                        <button
                            onClick={onDismiss} // Use the passed callback
                            className="absolute top-1.5 right-1.5 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded-full"
                            aria-label="Dismiss TL;DR summary"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                                <svg className="animate-spin h-4 w-4 text-indigo-600 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                <span>Generating summary with Gemini...</span>
                            </div>
                        )}

                        {/* Error State */}
                        {!isLoading && error && (
                            <div className="text-sm text-red-700 dark:text-red-400">
                                <strong className="font-medium">Error:</strong> {error}
                            </div>
                        )}

                        {/* Success State */}
                        {!isLoading && tldrContent && (
                            <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200 leading-relaxed">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {tldrContent}
                                </ReactMarkdown>
                            </div>
                        )}
                         {/* Placeholder for initial state before loading/error/content */}
                         {!isLoading && !error && !tldrContent && (
                             <div className="text-sm text-gray-400 dark:text-gray-500 italic">Click "TL;DR" above to generate summary.</div>
                         )}
                    </div>
                )}
            </div>
        </div>
    ) : (
        <></>
    );
};

export default GeminiTldrCard;